import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export const PlaidOAuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [message, setMessage] = useState("Completing bank connection...");

  useEffect(() => {
    const handleOAuthRedirect = async () => {
      // Get the OAuth state token from URL
      const oauthStateId = searchParams.get("oauth_state_id");
      
      if (!oauthStateId) {
        // No OAuth state - this might be a direct navigation
        // Just redirect to onboarding
        navigate("/onboarding");
        return;
      }

      try {
        // The OAuth flow is handled by Plaid Link
        // We need to re-initialize Link with the received OAuth state
        // Store the state so onboarding can pick it up
        localStorage.setItem("plaid_oauth_state_id", oauthStateId);
        
        setMessage("Redirecting to complete connection...");
        
        // Redirect back to onboarding which will handle the OAuth completion
        setTimeout(() => {
          navigate("/onboarding?oauth=true");
        }, 1000);
        
      } catch (error) {
        console.error("OAuth redirect error:", error);
        setStatus("error");
        setMessage("Failed to complete bank connection");
        toast.error("Failed to complete bank connection. Please try again.");
        
        setTimeout(() => {
          navigate("/onboarding");
        }, 2000);
      }
    };

    handleOAuthRedirect();
  }, [searchParams, navigate, api]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardContent className="py-12 text-center space-y-6">
          {status === "processing" && (
            <>
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold mb-2">
                  Processing
                </h3>
                <p className="text-muted-foreground">{message}</p>
              </div>
            </>
          )}
          
          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold mb-2">
                  Connected!
                </h3>
                <p className="text-muted-foreground">{message}</p>
              </div>
            </>
          )}
          
          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-semibold mb-2">
                  Connection Failed
                </h3>
                <p className="text-muted-foreground">{message}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
